import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, Clock, XCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Loader, fmt, fmtDateTime, StatusBadge, EmptyState } from "@/components/shared";

const icons = { pending: [Clock, "text-amber-300 bg-amber-500/15"], approved: [CheckCircle2, "text-emerald-300 bg-emerald-500/15"], rejected: [XCircle, "text-red-300 bg-red-500/15"] };

function Row({ k, v, testId }) {
  return <div className="flex items-center justify-between gap-4 py-3 border-b border-white/5 last:border-0"><span className="text-sm text-slate-400">{k}</span><span className="text-sm text-slate-100 font-mono text-right break-all" data-testid={testId}>{v}</span></div>;
}

export default function ShareConfirmation() {
  const { kind, id } = useParams();
  const [rec, setRec] = useState(null);
  const reduce = useReducedMotion();
  useEffect(() => { api.get(`/shares/${kind === "deposit" ? "deposits" : "purchases"}/${id}`).then((r) => setRec(r.data)).catch(() => setRec(false)); }, [kind, id]);
  if (rec === null) return <Loader />;
  if (!rec) return <EmptyState title="Record not found" subtitle="This confirmation does not exist or does not belong to you." />;
  const [Icon, cls] = icons[rec.status] || icons.pending;
  const isDep = kind === "deposit";
  const headline = rec.status === "pending" ? (isDep ? "Deposit Received — Pending Verification" : "Purchase Request Received")
    : rec.status === "approved" ? (isDep ? "Deposit Verified — Shares Issued" : "Purchase Approved") : (isDep ? "Deposit Rejected" : "Purchase Rejected");

  return (
    <div className="max-w-2xl mx-auto" data-testid="share-confirmation-page">
      <motion.div initial={reduce ? false : { opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="glass rounded-2xl p-8 relative overflow-hidden">
        <span className="absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" aria-hidden="true" />
        <div className="relative text-center">
          <motion.div initial={reduce ? false : { scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.15 }}
            className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center ${cls}`}><Icon className="w-8 h-8" /></motion.div>
          <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400 mt-5">{isDep ? "Deposit for Shares" : "Share Purchase"}</p>
          <h1 className="font-heading text-2xl font-bold text-white mt-1" data-testid="confirmation-headline">{headline}</h1>
          <div className="mt-3"><StatusBadge status={rec.status} /></div>
        </div>
        <div className="relative mt-8">
          <Row k="Reference ID" v={rec.id} testId="confirmation-ref" />
          <Row k={isDep ? (rec.issued_shares != null ? "Shares Issued" : "Intended Shares") : "Shares"} v={isDep ? (rec.issued_shares ?? rec.intended_shares) : rec.quantity} testId="confirmation-shares" />
          <Row k="Share Price" v={`${fmt(rec.share_price)} USDT`} />
          <Row k={isDep ? "Deposit Amount" : "Total"} v={`${fmt(isDep ? rec.amount : rec.total)} USDT`} testId="confirmation-total" />
          {isDep && <Row k="Network" v={rec.network} />}
          {isDep && <Row k="Transaction ID" v={rec.transaction_hash} />}
          <Row k="Submitted" v={fmtDateTime(rec.created_at)} />
          {rec.reviewed_at && <Row k="Reviewed" v={fmtDateTime(rec.reviewed_at)} />}
          {rec.admin_note && <Row k="Note" v={rec.admin_note} />}
        </div>
        <p className="relative text-xs text-slate-500 mt-6 text-center">{rec.status === "pending" ? "An administrator will review this request. You will be notified once it is processed." : "This record is final. Contact support if you have questions."}</p>
        <div className="relative mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to={isDep ? "/my-share-deposits" : "/my-shares"} data-testid="confirmation-view-btn" className="btn-glow px-5 py-2.5 rounded-full text-sm font-semibold text-slate-950 inline-flex items-center justify-center gap-2">{isDep ? "My Share Deposits" : "My Shares"} <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/shares-portal" className="glass glass-hover px-5 py-2.5 rounded-full text-sm text-cyan-300 inline-flex items-center justify-center">Back to Company Shares</Link>
        </div>
      </motion.div>
    </div>
  );
}
